import {
  CODE_WORLD_CATALOGUE,
  WORLDS,
  capProblems,
  catalogueWorldOfPackage,
  checkWorldFormat,
  codeWorldProblems,
  codeWorldsOf,
  packageCost,
  readHeights,
  type PackageProblem,
  type ServedWorld,
  type WorldCatalogue,
  type WorldPackage,
  worldIn,
} from '@vrlearning/lesson-schema';
import type { Credit } from '../models/credits.js';
import { MANIFEST, worldUrl, type Manifest } from './manifest.js';
import { packageDefinition } from './package-world.js';
import { WORLD_DEFINITIONS } from './registry.js';
import type { WorldDefinition } from './world.js';

/** Where a lesson's world comes from (W11). */
export type WorldSource = {
  readonly kind: 'code' | 'pakket';
  readonly definition: WorldDefinition;
  readonly manifest: Manifest;
  readonly urlOf: (stored: string) => string;
  /** Who made what the world is made of (a package's own credits); the code worlds' are in the manifest. */
  readonly credits: readonly Credit[];
  /** A package's own checked JSON: what the lesson's questions about its world (features, placement) are answered from. */
  readonly servedPackage?: WorldPackage;
};

const CODE_WORLDS = codeWorldsOf(WORLDS);

/** As long as the code worlds' own loading waits for a file before giving up. */
const HEIGHTS_TIMEOUT_MS = 15_000;

/** [work], or an error "te langzaam" when it takes longer than [ms]. */
function within<T>(ms: number, work: Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('te langzaam')), ms);
    work.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error instanceof Error ? error : new Error(String(error)));
      },
    );
  });
}

/** The code world of a slug, if this headset still has one. */
export function worldSourceFor(slug: string): WorldSource | undefined {
  const definition = (WORLD_DEFINITIONS as Readonly<Record<string, WorldDefinition | undefined>>)[
    slug
  ];
  return definition
    ? {
        kind: 'code',
        definition,
        manifest: MANIFEST,
        urlOf: (stored) => worldUrl(stored),
        credits: [],
      }
    : undefined;
}

/**
 * A lesson's world, ready to build: the served package of its slug when there is one -- checked
 * again here (the headset understands every format it shipped and refuses the rest whole) and its
 * height map fetched -- else the code world, else none (the lesson shows the studio).
 */
export async function prepareWorld(
  slug: string,
  served: Readonly<Record<string, ServedWorld>> | undefined,
  media: Readonly<Record<string, string>>,
  fetchBytes: (url: string) => Promise<ArrayBuffer>,
): Promise<{ source?: WorldSource; problems: PackageProblem[] }> {
  const entry = served?.[slug];
  if (!entry) {
    const code = worldSourceFor(slug);
    return code ? { source: code, problems: [] } : { problems: [] };
  }
  const files = Object.entries(entry.files).map(([name, f]) => ({ name, bytes: f.bytes }));
  const format = checkWorldFormat(entry.package, files);
  const pkg = format.package;
  if (!pkg) return { problems: format.problems };
  if (pkg.slug !== slug) {
    return {
      problems: [{ path: '/slug', message: `is "${pkg.slug}", de les noemt de wereld "${slug}"` }],
    };
  }
  const sameWorld = codeWorldProblems(pkg, CODE_WORLDS);
  if (sameWorld.length > 0) return { problems: sameWorld };
  // What all its blocks together may ask of the headset (V3), before anything is fetched.
  const caps = capProblems(pkg);
  if (caps.length > 0) return { problems: caps };
  // Every file needs its signed address: nothing is fetched (never `fetch('')`) for a half-served package.
  for (const [name, f] of Object.entries(entry.files)) {
    if (!media[f.mediaId])
      return { problems: [{ path: `files/${name}`, message: 'heeft geen adres' }] };
  }
  // What its blocks declare must fit a Quest 3 (the GLBs are measured by the worker, not here).
  const cost = packageCost(pkg, null);
  if (cost.problems.length > 0) return { problems: cost.problems };
  const file = entry.files[pkg.terrain.heights.file];
  const url = file ? media[file.mediaId] : undefined;
  try {
    if (!url) throw new Error('geen adres');
    const heights = readHeights(
      new Uint8Array(await within(HEIGHTS_TIMEOUT_MS, fetchBytes(url))),
      pkg.terrain.heights,
    );
    return { source: packageDefinition({ ...entry, package: pkg }, media, heights), problems: [] };
  } catch (error) {
    return {
      problems: [
        {
          path: '/terrain/heights/file',
          message: `kon niet worden geladen (${(error as Error).message})`,
        },
      ],
    };
  }
}

/** The one world a lesson is in, as a catalogue (V10): its package's entry, or its code world's. */
export function catalogueOfSource(slug: string, source: WorldSource | undefined): WorldCatalogue {
  if (source?.kind === 'pakket' && source.servedPackage)
    return [catalogueWorldOfPackage(source.servedPackage, null)];
  const code = worldIn(CODE_WORLD_CATALOGUE, slug);
  return code ? [code] : [];
}
